import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChildren,
} from '@angular/core';
import {RequestService} from "../../../../core/services/request/request.service";
import {UtilService} from "../../../../core/services/util/util.service";
import {CatalogQuestionResponseModel} from "../../../../core/services/catalog-data/catalog-question-response.model";
import {DomSanitizer} from "@angular/platform-browser";
import {Subject, Subscription} from "rxjs";
import {CatalogQuestionModel} from "../../../../core/services/catalog-data/catalog-question.model";
import {CatalogDataModel} from "../../../../core/services/catalog-data/catalog-data.model";
import {CatalogDataService} from "../../../../core/services/catalog-data/catalog-data.service";
import * as fastDeepEqual from 'fast-deep-equal/es6';
import {detailedDiff} from 'deep-object-diff';
import {
  CatalogQuestionWithSectionModel
} from "../../../../core/services/catalog-data/catalog-question-with-section.model";
import {CatalogFieldComponent} from "../catalog-field/catalog-field.component";
import {CatalogQuestionDataModel} from "../../../../core/services/catalog-data/catalog-question-data.model";
import {$translate} from "../../../../core/services/internationalization/localize.service";
import {ActivatedRoute} from "@angular/router";
import {CatalogRequestCreatedEvent} from "./catalog-request-created-event.model";
import {FeatureFlagsService} from "../../../../core/services/feature-flag/feature-flags.service";
import {AlertService} from "../../../../core/services/alert/alert.service";
import {InternationalizationService} from "../../../../core/services/internationalization/internationalization.service";

// This type exists so that Angular change detection works properly. It uses object identity to determine
// if an object has changed. If we don't build a new object then it won't notice, however the questions are all
// from the allQuestions array and they never change.
interface CurrentCatalogQuestionModel {
  question: CatalogQuestionModel;
}

interface CurrentCatalogQuestionWithSectionModel {
  header: string;
  alternateColumnCountInSection: boolean;
  questions: CurrentCatalogQuestionModel[];
}

@Component({
  selector: 'sb-catalog-form',
  templateUrl: './catalog-form.component.html',
  styleUrls: ['./catalog-form.component.scss']
})
export class CatalogFormComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChildren(CatalogFieldComponent)
  catalogFields: CatalogFieldComponent[];

  @Input()
  item: CatalogDataModel;

  @Input()
  nluModel: any;

  @Input("submitted-event-only")
  submittedEventOnly: boolean = false;

  // This is the record id of the target record that is created by this catalog form
  @Input("initial-record-id")
  initialRecordID: string = null;

  // This is a JS object containing the fields of a record
  @Input("source-record")
  sourceRec: any = null;

  // This determines if sourceRec described a new record or an existing one
  @Input("source-is-new-record")
  sourceIsNewRecord: boolean = false;

  @Output()
  submitted = new EventEmitter<string>();

  @Output()
  requestCreated = new EventEmitter<CatalogRequestCreatedEvent>();

  // Set up a mechanism for us to communicate with the child components (e.g. to ask for validation)
  eventsSubject: Subject<any> = new Subject<any>();

  EMPTY_PARENT = "__empty_parent__";

  allQuestions: CatalogQuestionModel[] = [];

  allQuestionsById: {
    [id: string]: CatalogQuestionModel
  } = {};

  allQuestionsByParent: {
    [id: string]: CatalogQuestionModel[]
  } = { EMPTY_PARENT: [] };

  currentQuestions: CurrentCatalogQuestionModel[] = [];

  currentQuestionsWithSections: CurrentCatalogQuestionWithSectionModel[] = [];

  requestId: string;

  hasNext: boolean = true;

  progress: number = 0;

  unansweredMandatory = true;

  unansweredMandatoryQuestions: CurrentCatalogQuestionModel[] = [];

  unacknowledged = true;

  unacknowledgedQuestions: CurrentCatalogQuestionModel[] = [];

  fieldErrors = false;
  fieldErrorsQuestions: CurrentCatalogQuestionModel[] = [];

  errors: {
    errors?: string[],
    validationErrors?: string[],
    fieldValidationErrors?: string[]
  } = {
  };

  private activatedRoute$: Subscription | null;

  // To ensure jumping when reloading questions does not happen.
  private lastScrollPosition = null;

  constructor(private catalogDataService: CatalogDataService,
              private requestService: RequestService,
              private hostElement: ElementRef,
              private utilService: UtilService,
              private sanitizer: DomSanitizer,
              private activatedRoute: ActivatedRoute,
              private featureFlagsService: FeatureFlagsService,
              private alertService: AlertService,
              private i18nService: InternationalizationService) {
  }

  async ngOnInit() {
    const that = this;
    this.allQuestions = await this.catalogDataService.getAllQuestions(this.item.id);

    this.allQuestions.forEach((aq) => {
      aq.changed = new EventEmitter<CatalogQuestionModel>();
      aq.changedDefault = new EventEmitter<CatalogQuestionModel>();
      aq.subscription = aq.changed.subscribe(async (question: CatalogQuestionModel) => {
        this.debug() && console.log(`catalog: question subscription called for name=${question.name} value=${question.data.answer?.value} preserve=${question.preserve}`, question.data);

        this.checkUnansweredMandatory();
        this.checkUnacknowledged();

        // We proceed if there are no unanswered mandatory or unacknowledged questions, or we are changing the value
        // of an already answered question.
        if ((!this.unansweredMandatory && !this.unacknowledged) || question.data.answer) {
          const answers = {};
          answers[question.id] = question.data.answer.value;
          const precedingQuestionId = question.id;
          const triggeredQuestionId = question.id;
          await this.fetchAndProcessNextQuestionResponses(precedingQuestionId, triggeredQuestionId, answers);
        }
      });
      aq.subscriptionDefault = aq.changedDefault.subscribe(async (question: CatalogQuestionModel) => {
        this.debug() && console.log(`catalog: question subscriptionDefault called for name=${question.name} value=${question.data.answer?.value} preserve=${question.preserve}`, question.data);

        if (question.data.answer) {
          const answers = {};
          answers[question.id] = question.data.answer.value;
          const precedingQuestionId = question.id;
          const triggeredQuestionId = question.id;
          await this.sendAnswer(precedingQuestionId, triggeredQuestionId, answers, false);
        }
      });

      this.allQuestionsById[aq.id] = aq;

      const precedingQuestionId = aq.precedingQuestionId || this.EMPTY_PARENT;
      this.allQuestionsByParent[precedingQuestionId] = this.allQuestionsByParent[precedingQuestionId] || [];
      this.allQuestionsByParent[precedingQuestionId].push(aq);
    });

    const firstQuestions: CatalogQuestionResponseModel = await this.catalogDataService.getFirstQuestions(this.item.id, this.nluModel, this.initialRecordID, this.sourceRec, this.sourceIsNewRecord);

    this.currentQuestions = firstQuestions.questions.map((cq) => {
      const question = this.allQuestionsById[cq.questionId];
      question.data = cq;

      // Do not preserve the current value of this item
      question.preserve = false;

      return { question: question };
    });

    this.groupCurrentQuestionsBySection();

    this.checkUnansweredMandatory();
    this.checkUnacknowledged();

    this.requestId = firstQuestions.requestId;
    this.hasNext = firstQuestions.hasNext;
    this.progress = firstQuestions.progress;

    this.catalogDataService.setCurrentCatalogRequestId(this.requestId);

    // Needed for attachments
    this.allQuestions.forEach((aq) => {
      aq.requestId = this.requestId;
      aq.table = this.item.table;
    });

    this.activatedRoute$ = this.activatedRoute.queryParamMap.subscribe(async params => {
      const sourceRecId = params.get('sourceRecId');
      const sourceRecTable = params.get('sourceRecTable');
      const sourceIsNewRecord = params.get('sourceIsNewRecord');
      const initialRecordId = params.get('initialRecordId');
      if (sourceRecId || sourceRecTable) {
        this.sourceRec = {};
        if (sourceRecId) {
          this.sourceRec.id = sourceRecId;
        }
        if (sourceRecTable) {
          this.sourceRec.TableClass = sourceRecTable;
        }
      }
      if (sourceIsNewRecord != null) {
        this.sourceIsNewRecord = this.utilService.toBoolean(sourceIsNewRecord);
      }
      if (initialRecordId) {
        this.initialRecordID = initialRecordId;
      }
    });
  }

  private async fetchAndProcessNextQuestionResponses(precedingQuestionId: string, triggeredQuestionId: string, answers: {}) {
    this.saveScrollPosition();
    const nextQuestions: CatalogQuestionResponseModel = await this.catalogDataService.getNextQuestions(this.item.id, this.requestId, precedingQuestionId, triggeredQuestionId, answers, this.nluModel, this.sourceRec, this.sourceIsNewRecord);
    this.debug() && console.log(`catalog: fetchAndProcessNextQuestionResponses called for id=${this.formatId(precedingQuestionId)}`, this.formatAnswers(answers), this.formatQuestions(nextQuestions.questions, ['RNHAccessories']));

    // We have to convert some values from string to boolean to ensure that the existing items and the next questions can
    // be compared correctly otherwise each boolean field will trigger another reload as it is built from scratch again.
    // This replaces the code in catalog-field.component.ts:134 function convertValueTypeIfRequired
    nextQuestions.questions.forEach((q) => {
      const question = this.allQuestionsById[q.questionId];
      if (question.datatype === "acknowledgement" || question.datatype === "boolean") {
        if (q.answer && q.answer.value && typeof q.answer.value === "string") {
          (q.answer.value as unknown as boolean) = this.utilService.toBoolean(q.answer.value);
        }
        if (q.defaultValue) {
          if (q.defaultValue.value && typeof q.defaultValue.value === "string") {
            (q.defaultValue.value as unknown as boolean) = this.utilService.toBoolean(q.defaultValue.value);
          }
        }
      }
    });

    // We have to copy across the value of the 'saved' flag because it is also triggering another reload. The value in the browser should always
    // match the one in the server, but rebuilding the field is not necessary in this case.
    nextQuestions.questions.forEach((q) => {
      const question = this.allQuestionsById[q.questionId];
      if (question.data) {
        if (q.saved !== question.data.saved) {
          question.data.saved = q.saved;
        }
      }
    });

    // We have to copy across the value of the 'validationMessage' flag. The value in the browser should always
    // match the one in the server, but rebuilding the field is not necessary in this case.
    nextQuestions.questions.forEach((q) => {
      const question = this.allQuestionsById[q.questionId];
      if (question.data) {
        if (q.validationMessage !== question.data.validationMessage) {
          question.data.validationMessage = q.validationMessage;
        }
      }
    });

    // We need to split nextQuestions into two parts those before (and including precedingQuestionId) and those afterwards
    // The ones before are kept from the currentQuestions array, while the ones after are rebuilt.
    const precedingQuestionIndex = nextQuestions.questions.findIndex((nq) => {
      return nq.questionId == precedingQuestionId;
    });

    // Get a map of the current questions so we can update the model, rather than rebuild it completely
    let currentQuestionsByID = this.currentQuestions.reduce( (map, questionModel) => {
      map[questionModel.question.id] = questionModel;
      return map;
    }, {});

    this.currentQuestions = nextQuestions.questions.map((nq, idx) => {
      let currentQuestionID = nq.questionId;
      const allQuestionsStoredItem = this.allQuestionsById[currentQuestionID];

      // Check the readonly and mandatory and mandatory flags as they may have been changed by the post-processing script. Return what preserve should be
      function handleReadonlyAndMandatory(existingItem: CatalogQuestionModel, nq: CatalogQuestionDataModel, defaultPreserve: boolean): boolean {
        let preserve = defaultPreserve;
        if (existingItem.data.readonly != nq.readonly || existingItem.data.mandatory != nq.mandatory) {
          existingItem.data.readonly = nq.readonly;
          existingItem.data.mandatory = nq.mandatory;
          preserve = false;
        }
        return preserve;
      }

      if (idx <= precedingQuestionIndex) {

        const existingItem = this.currentQuestions[idx];
        // The existing item may not have the display value set, so we need to copy it across.
        if (nq.answer && nq.answer.displayValue && existingItem.question.data.answer && !existingItem.question.data.answer.displayValue) {
          existingItem.question.data.answer.displayValue = nq.answer.displayValue;
        }
        // We need to copy across the default value from nq in case AlwaysUseDefault is set.
        if (existingItem.question.alwaysUseDefault) {
          existingItem.question.data.defaultValue = nq.defaultValue;
          existingItem.question.data.defaultValueSource = nq.defaultValueSource;
        }

        // Preserve the current value of this item
        existingItem.question.preserve = handleReadonlyAndMandatory(existingItem.question, nq, true);

        return existingItem;
      } else {
        // Check to see if we have an existing definition, and that the value hasn't been updated
        if (currentQuestionsByID[currentQuestionID] != null) {
          let existingItem = currentQuestionsByID[currentQuestionID];

          // Return the existing item if it is identical to the new one.
          // We remove the displayValue field as it is sometimes not present in the existing items.
          const lhs = this.utilService.omit(this.utilService.clone(existingItem.question.data), ['displayValue']);
          const rhs = this.utilService.omit(this.utilService.clone(nq), ['displayValue']);
          if (fastDeepEqual(lhs, rhs)) {

            // The existing item may not have the display value set, so we need to copy it across
            if (nq.answer && nq.answer.displayValue && !existingItem.question.data.answer.displayValue) {
              existingItem.question.data.answer.displayValue = nq.answer.displayValue;
            }

            // Preserve the current value of this item
            existingItem.question.preserve = handleReadonlyAndMandatory(existingItem.question, nq, true);

            return existingItem;
          } else {
            const d = detailedDiff(lhs, rhs);
            this.debug() && console.log(`catalog: questions not equal: ${existingItem.question.name}`, lhs, rhs, d);
          }
        }

        // Fall through if not equal

        allQuestionsStoredItem.data = nq;

        // Do not preserve the current value of this item
        allQuestionsStoredItem.preserve = handleReadonlyAndMandatory(allQuestionsStoredItem, nq, false);

        return {question: allQuestionsStoredItem};
      }
    });

    this.groupCurrentQuestionsBySection();

    this.hasNext = nextQuestions.hasNext;
    this.progress = nextQuestions.progress;

    // Propagate validation messages to the KO components
    await this.eventsSubject.next("validationMessages");
    this.restoreScrollPosition();
  }

  private async sendAnswer(precedingQuestionId: string, triggeredQuestionId: string, answers: {}, deleteAnswers: boolean = false) {
    this.debug() && console.log(`catalog: sendAnswer called for id=${this.formatId(precedingQuestionId)} delete=${deleteAnswers}`, this.formatAnswers(answers));

    if (this.requestId) {
      await this.catalogDataService.sendAnswer(this.item.id, this.requestId, precedingQuestionId, triggeredQuestionId, answers, deleteAnswers);
    }
  }

  ngOnDestroy() {
    this.allQuestions.forEach((q) => {
      q.subscription.unsubscribe();
      q.subscriptionDefault.unsubscribe();
    });

    this.activatedRoute$ && this.activatedRoute$.unsubscribe();
  }

  private saveScrollPosition() {
    this.lastScrollPosition = document.querySelector("#detailMain")?.scrollTop;
  }

  private restoreScrollPosition() {
    if (this.lastScrollPosition != null) {
      document.querySelector("#detailMain").scrollTo({top: this.lastScrollPosition, behavior: 'smooth'});
    }
  }

  progressStyle(): string {
    return `${this.progress}%`;
  }

  ngAfterViewInit(): void {
  }

  buttonText() {
    if (this.item.directToLink) {
      return this.item.localizedButtonText || this.item.buttonText || $translate("ng.component.catalog-form.button.link")`Go to page`;
    } else {
      return (this.progress !== 100 || this.unansweredMandatory || this.unacknowledged || this.fieldErrors) ?
        $translate("ng.component.catalog-form.button.next")`Next` :
        this.item.localizedButtonText || this.item.buttonText || $translate("ng.component.catalog-form.button.submit")`Submit`;
    }
  }

  linkHref() {
    let link;
    if (this.item.link) {
      link = this.item.link.startsWith("#/") ? this.item.link : `#/${this.item.link}`;
    } else {
      link = `#/${this.item.table}/_create`;
    }
    return link;
  }

  checkUnansweredMandatory() {
    this.unansweredMandatory = false;
    this.unansweredMandatoryQuestions = [];

    this.currentQuestionsWithSections.forEach((s) => {
      s.questions.forEach(q => {
        if (this.isMandatory(q.question) && this.answerIsEmpty(q.question)) {
          this.unansweredMandatory = true;
          this.unansweredMandatoryQuestions.push(q);
        }
      });
    });
  }

  answerIsEmpty(question: CatalogQuestionModel): boolean {
    let answer = question.data.answer;

    if (answer == null) return true;
    if (typeof answer == "string" && (answer as string).trim() === "") return true;
    if (answer.value === undefined) return true;
    if (answer.value !== undefined && (answer.value == null || answer.value == "")) return true;

    return false;
  }

  // Determine if there are any unacknowledged questions. An unanswered question is also considered unacknowledged.
  checkUnacknowledged() {
    this.unacknowledged = false;
    this.unacknowledgedQuestions = [];

    this.currentQuestionsWithSections.forEach((s) => {
      s.questions.forEach(q => {
        if (q.question.datatype == "acknowledgement" && ((q.question.data.answer && `${q.question.data.answer.value}` != "true") || !q.question.data.answer)) {
          this.unacknowledged = true;
          this.unacknowledgedQuestions.push(q);
        }
      });
    });
  }

  checkFieldErrors() {
    this.fieldErrors = false;
    this.fieldErrorsQuestions = [];

    // Impl that looks at data from server
    this.currentQuestionsWithSections.forEach((s) => {
      s.questions.forEach(q => {
        q.question.data.validationMessage
        if (q.question.data.validationMessage) {
          this.fieldErrors = true;
          this.fieldErrorsQuestions.push(q);
        }
      });
    });

    // Alternative impl that looks at fields on form
    // this.catalogFields.forEach((f) => {
    //   if(f.koFieldModel.value.hasError()) {
    //     this.fieldErrors = true;
    //   }
    // });
  }

  async next() {
    // Initiate validation of the KO components
    await this.eventsSubject.next("validate");

    // Propagate server side validation messages to the KO components
    await this.eventsSubject.next("validationMessages");

    this.checkUnansweredMandatory();
    this.checkUnacknowledged();
    this.checkFieldErrors();

    if (this.progress < 100 || this.unansweredMandatory || this.unacknowledged || this.fieldErrors) {

      // Show alerts for issues. We only want to see one at a time.
      if (this.unansweredMandatory) {
        const msg = "<ul><li>" + this.unansweredMandatoryQuestions.map(q => this.i18nService.getMessage(q.question.question)).join("</li><li>") + "</li></ul>";
        const title = $translate("ng.component.catalog-form.mandatory-questions-title")`You cannot proceed until you have answered these mandatory questions`;
        this.alertService.infoMessage(msg, title);
      } else if (this.unacknowledged) {
        const msg = "<ul><li>" + this.unacknowledgedQuestions.map(q => this.i18nService.getMessage(q.question.question)).join("</li><li>") + "</li></ul>";
        const title = $translate("ng.component.catalog-form.acknowledgement-questions-title")`You cannot proceed until you have answered these acknowledgement questions`;
        this.alertService.warningMessage(msg, title);
      } else if (this.fieldErrors) {
        const msg = "<ul><li>" + this.fieldErrorsQuestions.map(q => this.i18nService.getMessage(q.question.question)).join("</li><li>") + "</li></ul>";
        const title = $translate("ng.component.catalog-form.error-questions-title")`You cannot proceed until you have corrected these answers with errors`;
        this.alertService.warningMessage(msg, title);
      }

      // This is a next button, send all the answers we have.
      const answers = {};
      let precedingQuestionId;

      this.currentQuestionsWithSections.forEach((s) => {
        s.questions.forEach(q => {
          if (q.question.data.answer) {
            answers[q.question.id] = q.question.data.answer.value;
            precedingQuestionId = q.question.id;
          }
        });
      });

      // Don't action the next button if we have no answers.
      if (precedingQuestionId) {
        await this.fetchAndProcessNextQuestionResponses(precedingQuestionId, null, answers);
      }

    } else {
      // This is a submit button.
      if (this.submittedEventOnly) {
        this.submitted.emit(this.requestId);
      } else {
        const requestSubmitResponse = await this.catalogDataService.submitRequest(this.item.id, this.requestId, this.sourceRec, this.sourceIsNewRecord);

        if (!requestSubmitResponse.created) {
          this.errors = requestSubmitResponse.errors;
          return;
        } else {
          this.errors = {};
        }
        this.requestCreated.emit({
          requestId: this.requestId,
          requestTableName: requestSubmitResponse.requestTableName,
          requestLink: requestSubmitResponse.requestLink
        } as CatalogRequestCreatedEvent);
        this.submitted.emit(this.requestId);
      }
    }
  }

  columnClass(alternateColumnCountInSection: boolean): string {
    const columnCount = this.item.twoColumns ? (alternateColumnCountInSection ? 1 :  2) : (alternateColumnCountInSection ? 2 : 1);
    return columnCount == 2 ? "quick-layout-half-width" : "quick-layout-full-width";
  }

  customContainerClass(question: CurrentCatalogQuestionModel): string {
    return question?.question?.rendererProperties?.['compact'] == 'true' ? 'l-compact' : '';
  }

  groupCurrentQuestionsBySection() {
    let currentSection: CatalogQuestionWithSectionModel = { header: null, alternateColumnCountInSection: false, questions: [] };
    let allSections = [currentSection];

    // Group all questions into their sections - this is so that sections still exist even if the
    // first question is not there.
    this.allQuestions.forEach((q, idx) => {
      if (q.localizedSectionTitle || q.sectionTitle) {
        // If we have another section header then open a new section
        currentSection = { header: q.localizedSectionTitle || q.sectionTitle, alternateColumnCountInSection: q.alternateColumnCountInSection,  questions: [q] };
        allSections.push(currentSection);
      } else {
        // Add to current section
        currentSection.questions.push(q);
      }

      // If we are told to close the section then add a new empty one
      if (q.closeSectionAfter) {
        currentSection = { header: null, alternateColumnCountInSection: false, questions: [] };
        allSections.push(currentSection);
      }
    });

    // Delete answers that are hidden and also have ClearAnswerIfHidden set
    const answersToDelete: CatalogQuestionModel[] = [];

    // Remove questions that are not to be displayed and sections that are empty, and replace questions with the ones from the
    // current questions.
    let sections: CurrentCatalogQuestionWithSectionModel[] = allSections.map((section, idx) => {
      const newSection: CurrentCatalogQuestionWithSectionModel = {
        header: section.header,
        alternateColumnCountInSection: section.alternateColumnCountInSection,
        questions: section.questions.map(sq => this.currentQuestions.find(q => q.question.id == sq.id)).filter(s => !!s)
      };
      // Hide the section if the first question is hidden and hideSectionIfHidden is set on the question
      const hideSection = (section.questions.length > 0) &&
                          (section.questions[0].hideSectionIfHidden && newSection.questions.find(q => q.question.id == section.questions[0].id) == null);

      if (hideSection) {
        const candidateAnswers = section.questions.filter(q => (this.item.clearAnswersIfHidden == true || q.clearAnswerIfHidden == true));
        answersToDelete.push(...candidateAnswers);
      }

      return (newSection.questions.length > 0 && !hideSection) ? newSection : null;
    }).filter( s => !!s);

    let sectionChanged = false;

    if (this.currentQuestionsWithSections.length !== sections.length) {
      sectionChanged = true;
    }

    if (this.currentQuestionsWithSections.length === sections.length) {
      this.currentQuestionsWithSections.forEach((s, idx) => {
        if (s.header !== sections[idx].header) {
          sectionChanged = true;
        }

        if (s.questions.length !== sections[idx].questions.length) {
          sectionChanged = true;
        }

        if (s.questions.length === sections[idx].questions.length) {
          s.questions.forEach((q, qi) => {
            if (q.question.id !== sections[idx].questions[qi].question.id) {
              sectionChanged = true;
            }
          });
        }
      });
    }

    if (sectionChanged) {
      this.currentQuestionsWithSections = sections;
    } else {
      this.currentQuestionsWithSections.forEach((s, idx) => {
        s.questions = sections[idx].questions;
      });
    }

    if (answersToDelete.length > 0) {
      const answers = answersToDelete.reduce((acc, curr) => {
        acc[curr.id] = curr.id;
        return acc;
      }, {});

      this.sendAnswer(null, null, answers, true);
    }
  }

  private isReadonly(field: CatalogQuestionModel) {
    const byAnswer = field?.data?.readonly;
    const byQuestion = field.readonly;
    return byAnswer != null ? byAnswer : byQuestion;
  }

  private isMandatory(field: CatalogQuestionModel) {
    const byAnswer = field?.data?.mandatory;
    const byQuestion = field.mandatory;
    return byAnswer != null ? byAnswer : byQuestion;
  }

  private formatAnswers(answers: any ) {
    return Object.entries(answers).map(e => {
      return `${this.formatId(e[0])}=${e[1]}`;
    });
  }

  private formatId(id: string) {
    return this.allQuestionsById[id].name;
  }

  private formatQuestions(questions: CatalogQuestionDataModel[], keys: string[] = []) {
    return questions.filter(q => keys.includes(q.questionName) || keys.length == 0).map(q => (({answer, defaultValue }) => ({ answer, defaultValue }))(q)).map(q => JSON.stringify(q));
  };

  private debug(): boolean {
    return this.featureFlagsService.isOn('verbose-logging');
  }
}
